import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, { ComponentProps, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Pressable, StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { AddMembersSection } from '@components/Common/AddMembersSection';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import {
  getSubmitButtonBg,
  getTaskCreateSubmitState,
  shouldShowSubmitSpinner,
} from '@components/Tasks/taskCreateSubmitState';
import { FormValues } from '@components/Tasks/TaskFormStack';
import {
  useGetProjectLazyQuery,
  useListTasksNamesUnderProjectLazyQuery,
} from '@graphql/generated';
import useColors from '@hooks/useColors';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTaskCreateFooter from '@hooks/useTaskCreateFooter';
import Images from '@themes/images';
import theme from '@themes/theme';

type TaskCreateFormModalProps = {
  onProjectPress: () => void;
  onDatePress: () => void;
  pickFiles: () => void;
  pickImages: () => void;
  launchCamera: () => void;
  parentProject?: boolean;
  taskname?: string;
  onSave?: () => void;
  isAssignTaskToChat?: boolean;
  onCancel?: () => void;
  bottomSheetTaskFormRef?: React.RefObject<BottomSheetModalMethods | null>;
  onFormValidityChange?: (isFormBlank: boolean) => void;
  setNoNeedShowDiscard: React.Dispatch<React.SetStateAction<boolean>>;
} & ComponentProps<typeof AddMembersSection>;

export const TaskCreateModalFooter: React.FC<TaskCreateFormModalProps> = ({
  onProjectPress,
  onDatePress,
  pickFiles,
  pickImages,
  launchCamera,
  onSave,
  onFormValidityChange,
  bottomSheetTaskFormRef,
  isAssignTaskToChat = false,
  setNoNeedShowDiscard,
  ...addMembersSectionProps
}) => {
  const { isReadySubmitting, setIsAddAssignee } = useTaskCreateFooter();
  const { selectedMembers } = useSelectedMembers();
  const { t } = useTranslation();
  const {
    isVisible,
    projectTutorialState,
    setProjectTutorialStateAndInStorage,
  } = useProjectTutorial();
  const { getMappedColor } = useColors();

  const { values, errors, isSubmitting, isValid, setFieldValue } =
    useFormikContext<FormValues>();

  const { projectId, projectUUId, name } = values;

  const toastRef = useRef<Toast>(null);

  const areAllFieldsBlank = () => {
    const { name, description, dueDate, files } = values;

    return (
      !name.trim() &&
      !description?.trim() &&
      !dueDate &&
      files?.length === 0 &&
      selectedMembers?.length === 0
    );
  };

  const [getProjectLazy, { data }] = useGetProjectLazyQuery({
    variables: {
      id: projectUUId ?? '',
    },
  });
  const { getProject: selectedProject } = data || {
    selectedProject: undefined,
  };

  const [listTasksNamesUnderProject, { loading: loadingTasksNameData }] =
    useListTasksNamesUnderProjectLazyQuery();

  const dismissModal = () => {
    if (bottomSheetTaskFormRef?.current) {
      setTimeout(() => {
        bottomSheetTaskFormRef?.current?.dismiss();
      }, 1000);
    }
  };

  const handleSave = async () => {
    try {
      const queryResult = await listTasksNamesUnderProject({
        variables: {
          projectId: projectId,
          first: selectedProject
            ? selectedProject!.tasksCount + selectedProject!.completedTasksCount
            : 100,
        },
        fetchPolicy: 'cache-and-network',
      });
      const data = queryResult.data;
      if (
        data?.listTasks?.edges?.some((task) => task?.node?.name === values.name)
      ) {
        Alert.alert(
          t('models:tasks.duplicateTask'),
          t('models:tasks.duplicateTaskMsg'),
          [
            { text: t('shared:cancel') },
            {
              text: t('models:tasks.proceed'),
              style: 'cancel',
              onPress: () => {
                submitForm();
              },
            },
          ]
        );
      } else {
        submitForm();
      }
    } catch {
      Alert.alert(
        t('shared:error'),
        'Error happened when submit, Please try again.'
      );
    }
  };

  const submitForm = () => {
    onSave?.();
  };

  const saveProjectTutorializationState = async (currentState: string) => {
    setProjectTutorialStateAndInStorage(currentState);
  };

  useEffect(() => {
    const isFormBlank = areAllFieldsBlank();
    onFormValidityChange?.(isFormBlank);
  }, [values, errors]);

  useEffect(() => {
    if (projectId) {
      getProjectLazy({
        variables: {
          id: projectId,
        },
      });
    } else if (projectUUId) {
      getProjectLazy({
        variables: {
          id: projectUUId,
        },
      });
    }
  }, [projectId, projectUUId]);

  useEffect(() => {
    if (data?.getProject?.id && !projectId) {
      setFieldValue('projectId', data.getProject.id);
    }
  }, [data?.getProject?.id]);

  const { description } = values;

  useEffect(() => {
    if (projectTutorialState && isVisible) {
      if (description && description?.length > 0) {
        if (Number(projectTutorialState) > 11) {
          saveProjectTutorializationState(projectTutorialState?.toString());
          return;
        }
      }
    }
  }, [description?.length, projectTutorialState, isVisible]);

  const [memberCount, setMemberCount] = useState(0);

  useEffect(() => {
    setMemberCount(selectedMembers?.length || 0);
    if (
      (selectedMembers?.length || memberCount) &&
      projectTutorialState === '12' &&
      isVisible
    ) {
      setProjectTutorialStateAndInStorage('13');
    }
  }, [selectedMembers, memberCount, projectTutorialState, isVisible]);

  const onPressAddMembers = () => {
    if (isReadySubmitting) return;
    setNoNeedShowDiscard(true);
    setIsAddAssignee(true);
    navigation.navigate('tasks-stack', {
      screen: 'task-members-add',
      params: {
        selectedProjectId: values.projectId,
        isCreateTaskForMobile: true,
        // here when isAssignTaskToChat, always go to Assign Member page
        memberCount: isAssignTaskToChat ? 0 : memberCount,
        addMembersSectionProps: addMembersSectionProps,
      },
    });
  };

  const navigation = useNavigation();

  // Name is not sliced here — the Text renders up to 2 lines and
  // ellipsizes anything longer (TA-10209 design update, Figma 154:28789)
  const getProjectName = () => selectedProject?.name ?? '';

  // selectedProject is stale Apollo lazy-query data that doesn't clear when
  // projectId/projectUUId reset (e.g. after task creation resets the form) —
  // gate on the id, same as the project name label, so the icon falls back
  // to the default Folder/grey05 instead of showing the old project's icon.
  const hasSelectedProject = !!(projectId || projectUUId) && !!selectedProject;

  const getProjectIcon = () => {
    if (!hasSelectedProject) return 'Folder';
    if (selectedProject?.icon)
      return selectedProject.icon as keyof typeof Images;
    return 'BuildingHomeHouse';
  };

  // When a project is selected, its own color drives the icon (per TA-10209
  // design update) — bypasses the shared Icon wrapper since it only accepts
  // theme color keys, not the raw hex getMappedColor returns.
  const getProjectIconElement = () => {
    if (hasSelectedProject && selectedProject?.color && !isReadySubmitting) {
      const ProjectIconSvg = Images[getProjectIcon()];
      return (
        <ProjectIconSvg
          color={getMappedColor(selectedProject.color)}
          width={16}
          height={16}
        />
      );
    }

    return (
      <Icon
        name={getProjectIcon()}
        color={isReadySubmitting ? 'grey04' : 'grey05'}
        variant='s'
      />
    );
  };

  // Compute disabled state internally using isValid and isSubmitting from Formik context
  // This avoids needing these values passed as props, which would cause parent callback recreation
  const { isDisabled, showDisabledBg } = getTaskCreateSubmitState({
    isValid,
    isSubmitting,
    name,
    projectId,
    loadingTasksNameData,
    errors,
  });

  const getDueDate = () => {
    if (!values.dueDate) return '';
    const endD = dayjs(values.dueDate);
    const endYear = endD.format('YYYY');
    return endD.format(
      endYear === new Date().getFullYear().toString() ? 'MMM D' : 'MMM D YYYY'
    );
  };

  const getMemberAvatar = () => {
    if (memberCount === 0) {
      return <Icon name='UserPlus' color='grey05' />;
    } else if (selectedMembers?.[0]?.__typename === 'StringSearchType') {
      return <EmailAvatar width={40} />;
    } else {
      return (
        <Avatar
          id={selectedMembers?.[0]?.id}
          avatar={selectedMembers?.[0]?.avatar}
          size='medium'
          label={selectedMembers?.[0]?.name}
          onPress={onPressAddMembers}
        />
      );
    }
  };

  return (
    <Box flex={1}>
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps='handled'
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}>
        <Box flex={1}>
          {/* TA-10920 #11: onboarding's first task only needs a name and a
              project, so the attachment/date/member actions are hidden. */}
          {!values.isOnboardingTask && (
            <Box
              flexDirection='row'
              borderColor='grey02'
              borderTopWidth={1}
              borderBottomWidth={1}
              paddingVertical='s'
              paddingHorizontal='xs'>
              <Box
                flex={1}
                flexDirection='row'
                alignItems='center'
                justifyContent='center'>
                <Box flex={1} style={{ paddingHorizontal: theme.spacing.xxs }}>
                  <Pressable
                    disabled={isReadySubmitting}
                    accessibilityLabel='Add File'
                    onPress={pickFiles}
                    hitSlop={2}
                    style={({ pressed }) => [
                      styles.footerIconButton,
                      pressed && !isReadySubmitting && styles.pressedBackground,
                    ]}>
                    <Icon
                      name='Paperclip'
                      color={isReadySubmitting ? 'grey04' : 'grey05'}
                    />
                  </Pressable>
                </Box>
                <Box flex={1} style={{ paddingHorizontal: theme.spacing.xxs }}>
                  <Pressable
                    disabled={isReadySubmitting}
                    accessibilityLabel='Image - Attach library images'
                    onPress={pickImages}
                    hitSlop={2}
                    style={({ pressed }) => [
                      styles.footerIconButton,
                      pressed && !isReadySubmitting && styles.pressedBackground,
                    ]}>
                    <Icon
                      name='Image'
                      color={isReadySubmitting ? 'grey04' : 'grey05'}
                    />
                  </Pressable>
                </Box>
                <Box flex={1} style={{ paddingHorizontal: theme.spacing.xxs }}>
                  <Pressable
                    disabled={isReadySubmitting}
                    accessibilityLabel='Camera - Attach camera images'
                    onPress={launchCamera}
                    hitSlop={2}
                    style={({ pressed }) => [
                      styles.footerIconButton,
                      pressed && !isReadySubmitting && styles.pressedBackground,
                    ]}>
                    <Icon
                      name='Camera'
                      color={isReadySubmitting ? 'grey04' : 'grey05'}
                    />
                  </Pressable>
                </Box>
                <Box flex={1} style={{ paddingHorizontal: theme.spacing.xxs }}>
                  <Pressable
                    disabled={isReadySubmitting}
                    accessibilityLabel={t('models:tasks.create.dates.dueDate')}
                    onPress={onDatePress}
                    hitSlop={2}
                    style={({ pressed }) => [
                      styles.footerIconButton,
                      pressed && !isReadySubmitting && styles.pressedBackground,
                      values.dueDate && { minWidth: 60 },
                    ]}>
                    {!values.dueDate ? (
                      <Icon
                        name='Calendar'
                        color={isReadySubmitting ? 'grey04' : 'grey05'}
                      />
                    ) : (
                      <Box
                        px='xs'
                        py='xxs'
                        backgroundColor='black'
                        borderRadius='xxl'
                        alignItems='center'
                        justifyContent='center'>
                        <Text
                          variant='metadataSecondaryNew'
                          color='white'
                          style={{
                            textAlign: 'center',
                          }}>
                          {getDueDate()}
                        </Text>
                      </Box>
                    )}
                  </Pressable>
                </Box>
              </Box>
              <Box
                borderColor='grey02'
                borderLeftWidth={1}
                marginHorizontal='xs'
              />
              <Box
                style={{
                  // Outer positioning container only — must stay unclipped so
                  // the member-count badge below (intentionally offset
                  // right:-2/top:-2 to overlap the circle's corner) isn't cut
                  // off by the inner circle's overflow:hidden.
                  marginHorizontal: theme.spacing.xs,
                  width: 40,
                  height: 40,
                }}>
                <Box
                  borderRadius='xxl'
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Without this, Android doesn't reliably clip the
                    // background paint to the rounded corners (a known RN/
                    // Android quirk) — the box rendered as a square with the
                    // grey01 background overflowing straight past the radius.
                    overflow: 'hidden',
                    backgroundColor:
                      memberCount === 0 ? theme.colors.grey01 : undefined,
                    paddingHorizontal:
                      memberCount === 0 ? theme.spacing.xs : undefined,
                    paddingVertical:
                      memberCount === 0 ? theme.spacing.s : undefined,
                    // Fixed regardless of memberCount so the footer's measured
                    // height never changes when an assignee is added — letting
                    // it auto-size around the Avatar caused a few-px layout
                    // shift that jittered the sheet and the submit button.
                    width: 40,
                    height: 40,
                  }}>
                  <Pressable
                    disabled={isReadySubmitting}
                    accessibilityLabel={t('models:tasks.create.members.title')}
                    onPress={onPressAddMembers}
                    style={({ pressed }) => ({
                      opacity: pressed && !isReadySubmitting ? 0.6 : 1,
                    })}>
                    {getMemberAvatar()}
                  </Pressable>
                </Box>
                {isReadySubmitting && (
                  <Box
                    borderRadius='xl'
                    backgroundColor='grey04'
                    opacity={0.5}
                    alignItems='center'
                    justifyContent='center'
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 2,
                      width: 40,
                      height: 40,
                    }}></Box>
                )}
                {memberCount > 0 && (
                  <Box
                    borderRadius='xl'
                    backgroundColor={isReadySubmitting ? 'grey04' : 'black'}
                    alignItems='center'
                    justifyContent='center'
                    style={{
                      position: 'absolute',
                      right: -2,
                      top: -2,
                      width: 16,
                    }}>
                    <Text variant='labelRequired' color='white'>
                      {memberCount}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box
            flex={1}
            paddingRight='m'
            flexDirection='row'
            alignItems='center'>
            <Box flex={1}>
              <Pressable
                disabled={isReadySubmitting}
                accessibilityLabel={t(
                  'models:tasks.create.project.addToProject'
                )}
                onPress={onProjectPress}
                style={({ pressed }) => [
                  styles.projectRow,
                  pressed && !isReadySubmitting && styles.pressedBackground,
                ]}>
                <Box flexDirection='row' alignItems='center'>
                  {getProjectIconElement()}
                  <Text
                    variant='bodyTaskName'
                    color={isReadySubmitting ? 'grey04' : 'black'}
                    numberOfLines={2}
                    marginLeft='xs'
                    ellipsizeMode='tail'
                    flexShrink={1}>
                    {(projectId || projectUUId) && selectedProject
                      ? getProjectName()
                      : t('models:tasks.create.project.selectProject')}
                  </Text>
                  <Icon
                    name='ChevronDown'
                    variant='s'
                    marginHorizontal='xs'
                    color={isReadySubmitting ? 'grey04' : 'textPrimary'}
                  />
                </Box>
              </Pressable>
            </Box>
            <Box paddingVertical='s'>
              <Pressable
                hitSlop={2}
                onPress={() => {
                  if (!projectId) {
                    toastRef?.current?.hideAll();
                    toastRef?.current?.show(
                      t('models:tasks.selectAProjectFirst'),
                      {
                        duration: 2000,
                      }
                    );
                    return;
                  }
                  if (isVisible) {
                    setNoNeedShowDiscard(true);
                    setProjectTutorialStateAndInStorage('14');
                    submitForm();
                    dismissModal();
                    return;
                  }
                  handleSave();
                }}
                disabled={isDisabled || isReadySubmitting}
                accessibilityLabel={t('shared:save')}>
                {({ pressed }) => (
                  <Box
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='xxl'
                    bg={getSubmitButtonBg(
                      showDisabledBg || isReadySubmitting,
                      pressed
                    )}
                    width={40}
                    height={40}>
                    {shouldShowSubmitSpinner(
                      isSubmitting,
                      isReadySubmitting
                    ) ? (
                      <Spinner size={18} strokeWidth={2} color='white' />
                    ) : (
                      <Icon variant='xl' name='ArrowUp' color='white' />
                    )}
                  </Box>
                )}
              </Pressable>
            </Box>
          </Box>
        </Box>
        <Toast
          ref={toastRef}
          style={{
            borderRadius: theme.borderRadii.xs,
            backgroundColor: theme.colors.transparentBlack,
          }}
        />
      </KeyboardAwareScrollView>
    </Box>
  );
};

const styles = StyleSheet.create({
  footerIconButton: {
    width: 40,
    height: 40,
    borderRadius: theme.borderRadii.xxs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  projectRow: {
    borderRadius: theme.borderRadii.xxs,
    paddingLeft: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  pressedBackground: {
    backgroundColor: theme.colors.grey01,
  },
});
